<template>
  <DefaultModal
    ref="initiators_modal"
    @submit="submitInitiators"
    :hide-save-button="step == 1"
  >
    <template v-slot:title>
      <v-btn
        v-if="step == 2"
        @click="goBack"
        icon
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <span class="title-text left-align">
        <span class="headline">{{ $t('manage_initiators') }}</span>
        <span class="font-italic normal" v-if="jobDefinition"> - {{ jobDefinition.title }}</span>
      </span>
    </template>

    <template v-slot:content>
      <UserMultiSelect
        v-model="selectedUsersAndGroups"
        @selected-change="userOrGroupSelected"
        :items="normalizedUsersAndGroups"
        :loading="loadingUsersGroupsList"
        :rules="[v => (v && v.length > 0) || $t('form_rules.mandatory')]"
        label="add_users_and_groups"
        item-value="id"
        class="left-align"
      />

      <v-stepper
        v-model="step"
        class="custom-stepper left-align"
        outlined
        flat
      >
        <v-stepper-items>
          <v-stepper-content
            step="1"
            class="custom-stepper-content"
          >
            <v-list v-if="!loadingInitiatorsList">
              <span class="name-text list-name">{{ $t('current_initiators') }}</span>

              <v-list-item
                v-for="initiator in jobInitiators"
                :key="initiator.job_initiator_id"
                class="clear-left"
              >
                <v-list-item-avatar class="list-avatar" :class="initiator.user_group_id ? 'group-bg' : 'avatar'">
                  <template v-if="initiator.users_id">
                    <img
                      v-if="getUserAvatarUrl(initiator.users_id)"
                      :src="getUserAvatarUrl(initiator.users_id)"
                      :alt="`${initiator.users_id} avatar`"
                    >

                    <span v-else class="secondary--text headline text-uppercase font-weight-regular">{{ getUserName(initiator.users_id)[0] }}</span>
                  </template>

                  <v-icon v-else
                    color="secondary"
                  >mdi-account-multiple</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="title-text">
                    <span class="name-text">{{ initiator.users_id ? getUserName(initiator.users_id) : getGroupTitle(initiator.user_group_id) }}</span>
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-icon>
                  <v-btn
                    icon
                    color="grey darken-2"
                    @click="removeInitiatorModal(initiator)"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="jobInitiators.length === 0">
                <v-list-item-content>
                  <v-list-item-title class="text-center text--secondary">
                    {{ $t('no_initiators') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-stepper-content>

          <v-stepper-content
            step="2"
            class="custom-stepper-content"
          >
            <p class="subtitle-1">{{ $t('confirm_add_initiators') }}</p>
            <v-list>
              <v-list-item
                v-for="id in selectedUsersAndGroups"
                :key="id"
                class="clear-left"
              >
                <v-list-item-avatar class="list-avatar" :class="isGroup(id) ? 'group-bg' : 'avatar'">
                  <template v-if="isUser(id)">
                    <img
                      v-if="getUserAvatarUrlById(id)"
                      :src="getUserAvatarUrlById(id)"
                      :alt="`${id} avatar`"
                    >
                    <span v-else class="secondary--text headline text-uppercase font-weight-regular">{{ getUserNameById(id)[0] }}</span>
                  </template>

                  <v-icon v-else
                    color="secondary"
                  >mdi-account-multiple</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="title-text">
                    <span class="name-text">{{ getDisplayNameById(id) }}</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-stepper-content>

        </v-stepper-items>
      </v-stepper>

      <DefaultModal
        :title="$t('remove_initiator')"
        @submit="removeInitiator"
        ref="remove_initiator_confirm_modal"
        submit-button-text="confirm"
        deleteModal
      >
        <template v-slot:content>
          <span>{{ $t('initiator_removal_confirmation') }}</span>
          <span class="font-weight-bold"> {{ selectedInitiator.users_id ? getUserName(selectedInitiator.users_id): getGroupTitle(selectedInitiator.user_group_id) }}</span>?
        </template>
      </DefaultModal>

    </template>
  </DefaultModal>
</template>

<style scoped>
  .clear-left {
    padding: 0;
  }

  .left-align {
    margin: 0 16px;
  }

  .title-text {
    font-weight: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name-text {
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0.38px;
    font-weight: 500;
    color: #202124;
  }

  .list-name {
    font-family: Roboto;
    letter-spacing: 0.38px;
    color: #3C4043;
  }

  .custom-stepper {
    box-shadow: none;
  }

  .custom-stepper-content {
    padding: 0;
    height: 350px;
    overflow: auto;
  }

  .avatar {
    background-color: var(--v-primary-base) !important;
  }

  .group-bg {
    background-color: var(--v-primary-base) !important;
  }

  .list-avatar {
    justify-content: center;
    margin-bottom: 23px;
  }
</style>

<script>
import { mapActions } from 'vuex'

import DefaultModal from '@/components/DefaultModal'
import UserMultiSelect from '@/components/core/UserMultiSelect'
import AppActions from '@/store/app/actions-types'
import UserActions from '@/store/core/user/actions-types'
import GroupActions from '@/store/core/group/actions-types'

import JobInitiatorAPI from '@/services/api/job/job_initiator'

export default {
  name: 'JobDefinitionInitiators',

  components: {
    DefaultModal,
    UserMultiSelect,
  },

  data: () => ({
    loadingUsersGroupsList: false,
    loadingInitiatorsList: false,
    selectedUsersAndGroups: [],
    selectedInitiator: {},
    usersAndGroups: [],
    jobInitiators: [],
    jobDefinition: null,
    step: 1,
  }),

  computed: {
    normalizedUsersAndGroups() {
      return this.usersAndGroups.reduce((result, el) => {
        if (el.users_id) {
          if (this.jobInitiators.find(ji => ji.users_id == el.users_id))
            return result
        }

        if (el.user_group_id) {
          if (this.jobInitiators.find(ji => ji.user_group_id == el.user_group_id))
            return result
        }

        result.push({
          id: el.users_id ? `users-${el.users_id}` : `group-${el.user_group_id}`,
          idRaw: el.users_id || el.user_group_id,
          name: el.name || el.group_title,
          isUser: el.users_id ? true : false,
          isGroup: el.user_group_id ? true : false,
          email: el.email ? el.email : null
        })

        return result
      }, [])
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    ...mapActions('core/user', [
      UserActions.GET_ALL_USERS,
    ]),

    ...mapActions('core/group', [
      GroupActions.GET_ALL_GROUPS,
    ]),

    fetchData() {
      this.getUsersAndGroups()
      this.getInitiatorsList()
    },

    getInitiatorsList() {
      if (!this.jobDefinition || !this.jobDefinition.job_definition_id) {
        return
      }

      this.loadingInitiatorsList = true
      this.jobInitiators = []

      JobInitiatorAPI.getJobInitiatorsFromJobDefinition(this.jobDefinition.job_definition_id)
        .then((result) => {
          this.jobInitiators = result.data || []
          this.loadingInitiatorsList = false
        })
        .catch((error) => {
          console.error('Error loading initiators:', error)
          this.loadingInitiatorsList = false
        })
    },

    getUsersAndGroups() {
      if (!this.jobDefinition || !this.jobDefinition.tenant_id) {
        return
      }

      this.loadingUsersGroupsList = true
      this.selectedUsersAndGroups = []
      this.usersAndGroups = []

      const usersOptions = {
        'filter[tenant_id]': this.jobDefinition.tenant_id,
        'filter[include_foreign]': true,
        'filter[active]': true,
        'limit': 9999,
      }

      const groupsOptions = {
        'filter[tenant]': this.jobDefinition.tenant_id,
        'filter[include_foreign]': true,
        'filter[active]': true,
        'limit': 9999,
      }

      const usersPromise = this[UserActions.GET_ALL_USERS](usersOptions)
      usersPromise.then((result) => {
        this.usersAndGroups = [...this.usersAndGroups.concat(result.items)]
      })

      const groupsPromise = this[GroupActions.GET_ALL_GROUPS](groupsOptions)
      groupsPromise.then((result) => {
        this.usersAndGroups = [...this.usersAndGroups.concat(result.items)]
      })

      Promise.all([usersPromise, groupsPromise])
        .finally(() => this.loadingUsersGroupsList = false)
    },

    submitInitiators() {
      let options = []

      this.selectedUsersAndGroups.forEach(id => {
        const item = this.normalizedUsersAndGroups.find(el => el.id == id)

        options.push({
          job_definition_id: this.jobDefinition.job_definition_id,
          user_group_id: item.isGroup ? item.idRaw : null,
          users_id: item.isUser ? item.idRaw : null,
        })
      })

      // Create initiators one by one
      const promises = options.map(data => JobInitiatorAPI.createJobInitiator(data))

      Promise.all(promises)
        .then(() => {
          this.selectedUsersAndGroups = []
          this.getInitiatorsList()
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('initiators_added_successfully'))
        })
        .catch((error) => {
          console.error('Error adding initiators:', error)
        })
        .finally(() => {
          this.$refs.initiators_modal.submitting = false
          this.step = 1
        })
    },

    removeInitiatorModal(initiator) {
      this.selectedInitiator = {...initiator}
      this.$refs.remove_initiator_confirm_modal.openModal()
    },

    removeInitiator() {
      JobInitiatorAPI.deleteJobInitiator(this.selectedInitiator)
        .then(() => {
          const index = this.jobInitiators.findIndex(ji =>
            ji.users_id == this.selectedInitiator.users_id && ji.user_group_id == this.selectedInitiator.user_group_id
          )

          if (index >= 0) {
            this.jobInitiators.splice(index, 1)
          }

          this.selectedInitiator = {}
          this.$refs.remove_initiator_confirm_modal.closeModal()
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('initiator_removed_successfully'))
        })
        .catch((error) => {
          console.error('Error removing initiator:', error)
          this.$refs.remove_initiator_confirm_modal.setIsSubmitting(false)
        })
    },

    getUserAvatarUrl(userId) {
      return (this.usersAndGroups.find(el => el.users_id == userId)?.avatar_url) ?? null
    },

    getUserName(userId) {
      return (this.usersAndGroups.find(el => el.users_id == userId)?.name) ?? `${this.$t('user')} ID: ${userId}`
    },

    getGroupTitle(groupId) {
      return (this.usersAndGroups.find(el => el.user_group_id == groupId)?.group_title) ?? `${this.$t('group')} ID: ${groupId}`
    },

    getUserAvatarUrlById(id) {
      const item = this.normalizedUsersAndGroups.find(el => el.id == id)
      if (!item || !item.isUser) return null
      return this.getUserAvatarUrl(item.idRaw)
    },

    getUserNameById(id) {
      const item = this.normalizedUsersAndGroups.find(el => el.id == id)
      if (!item || !item.isUser) return ''
      return this.getUserName(item.idRaw)
    },

    getDisplayNameById(id) {
      const item = this.normalizedUsersAndGroups.find(el => el.id == id)
      if (!item) return ''
      return item.isUser ? this.getUserName(item.idRaw) : this.getGroupTitle(item.idRaw)
    },

    isUser(id) {
      const item = this.normalizedUsersAndGroups.find(el => el.id == id)
      return item?.isUser ?? false
    },

    isGroup(id) {
      const item = this.normalizedUsersAndGroups.find(el => el.id == id)
      return item?.isGroup ?? false
    },

    goBack() {
      this.step = 1
      this.clearForm()
    },

    userOrGroupSelected() {
      this.step = 2
    },

    clearForm() {
      this.selectedUsersAndGroups = []
    },

    open(jobDefinition) {
      this.jobDefinition = {...jobDefinition}
      this.step = 1

      this.clearForm()

      this.$refs.initiators_modal.openModal()

      this.fetchData()
    },

    close() {
      this.$refs.initiators_modal.closeModal()
    },
  },
}
</script>
